import React, { useEffect, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * Small fixed banner that appears whenever:
 *  - The browser reports we are offline, OR
 *  - We failed to verify the auth session due to a connectivity issue.
 *
 * On reconnect we show a brief "Conexión restablecida" toast for ~2s.
 */
const ConnectionStatus = () => {
  const { offlineSession } = useAuth();
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      setShowReconnected(true);
      const t = setTimeout(() => setShowReconnected(false), 3500);
      return () => clearTimeout(t);
    };
    const goOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const offline = !isOnline || offlineSession;

  if (!offline && !showReconnected) return null;

  return (
    <div
      className="fixed top-16 sm:top-20 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none px-2"
      data-testid="connection-status-banner"
    >
      {offline ? (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500 text-white shadow-lg text-xs sm:text-sm font-medium animate-fadeIn whitespace-nowrap">
          <WifiOff className="w-4 h-4 shrink-0" />
          <span>Sin conexión</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white shadow-lg text-sm font-semibold animate-fadeIn whitespace-nowrap">
          <Wifi className="w-4 h-4 shrink-0" />
          <span>¡En línea de nuevo!</span>
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;
